import type { BetResult, BetRejection } from "@/features/betslip/types";
import type {
  AcceptedBetResponse,
  AcceptedSelection,
  AnalyticsContext,
  BetResponse,
  BetslipStoreHandle,
  RejectedBetResponse,
} from "@/features/betslip/services/submitBet/types";
import { fireBetSubmittedTelemetry } from "@/features/betslip/services/submitBet/analytics";
import { getSubmittedOutcomeIds } from "@/features/betslip/services/submitBet/buildPayload";
import { triggerHaptic } from "@/utilities/haptics";

const parseRejectionReason = (template: string): string =>
  template.startsWith("[") && template.endsWith("]")
    ? template.slice(1, -1)
    : template;

type AcceptedResult = Extract<BetResult, { status: "accepted" }>;

const buildAcceptedResult = (resp: AcceptedBetResponse): AcceptedResult => ({
  status: "accepted",
  betId: resp.data.betId,
  entryId: resp.data.entryId,
  ...(resp.data.partialStake !== undefined && {
    partialStake: resp.data.partialStake,
  }),
  ...(resp.data.odds !== undefined && { odds: resp.data.odds }),
});

// Multi legs echo the per-leg correlationId we sent.
const toOddsByLeg = (
  selections: AcceptedSelection[] = [],
  correlationToOutcome: Record<string, string>,
) => {
  const oddsByLeg: Record<string, number> = {};
  for (const { correlationId, odds } of selections) {
    const outcomeId = correlationToOutcome[correlationId];
    if (outcomeId) oddsByLeg[outcomeId] = odds;
  }
  return oddsByLeg;
};

const buildRejection = (resp: RejectedBetResponse): BetRejection => ({
  reason: parseRejectionReason(resp.errors[0]?.messageTemplate ?? "unknown"),
  message: resp.errors[0]?.message ?? "Bet rejected",
  ...(resp.data.alternativeStake !== undefined && {
    alternativeStake: resp.data.alternativeStake,
  }),
});

/**
 * Match a multi-outcome response back to the slip selection that submitted
 * exactly that set of outcomeIds (SEM legs, exotic runner-positions,
 * quaddie legs). Exact-set semantics mirror legacy `findMultiOutcomeId`:
 * every echoed id belongs to the selection and the counts agree, so an
 * exacta in the slip can't swallow a trifecta's response.
 */
const findSelectionBySubmittedIds = (
  storeHandle: BetslipStoreHandle,
  outcomeIds: string[],
) => {
  const store = storeHandle.getState();
  const echoed = new Set(outcomeIds);
  return Object.values(store.selections).find((s) => {
    const submitted = getSubmittedOutcomeIds(s);
    return (
      submitted.length === echoed.size &&
      submitted.every((id) => echoed.has(id))
    );
  });
};

type ProcessContext = {
  storeHandle: BetslipStoreHandle;
  isMulti: boolean;
  multiCorrelationId?: string;
  /** Map from per-leg correlationId → outcomeId for multi rejections. */
  correlationToOutcome: Record<string, string>;
  /** Map from single-bet correlationId → slip selection key. */
  betCorrelationToSelection: Record<string, string>;
  analytics: AnalyticsContext;
};

/**
 * Resolve the slip selection key a single-bet response belongs to.
 * Route by the echoed outcomeId when there is exactly one, then by
 * exact-set matching of submitted ids, then fall back to the bet's
 * correlationId — the same last resort legacy uses for selections whose
 * echoed ids can't be matched (betslipSlice `getOutcomeIdByCorrelation`).
 */
const resolveSelectionKey = (
  context: ProcessContext,
  outcomeIds: string[],
  correlationId: string,
): string | undefined => {
  if (outcomeIds.length === 1) return outcomeIds[0];
  const matched = findSelectionBySubmittedIds(context.storeHandle, outcomeIds);
  return matched?.outcomeId ?? context.betCorrelationToSelection[correlationId];
};

/**
 * Slip keys of intercepted SINGLE bets, for per-card "under review"
 * marking. An intercepted multi or combo marks no cards — their review
 * state is carried by the footer overlay alone.
 */
export const resolveInterceptedOutcomeIds = (
  intercepted: BetResponse[],
  context: ProcessContext,
): string[] =>
  intercepted.flatMap((resp) => {
    const correlationId = resp.data.correlationId ?? "";
    if (context.isMulti && context.multiCorrelationId === correlationId) {
      return [];
    }
    if (context.analytics.comboCorrelationToType[correlationId]) return [];
    const key = resolveSelectionKey(
      context,
      resp.data.outcomeIds ?? [],
      correlationId,
    );
    return key ? [key] : [];
  });

export const processResponse = (
  resp: BetResponse,
  context: ProcessContext,
): void => {
  const store = context.storeHandle.getState();
  const outcomeIds = resp.data.outcomeIds ?? [];
  const respCorrelationId = resp.data.correlationId ?? "";
  const isMultiResponse =
    context.isMulti && context.multiCorrelationId === respCorrelationId;
  const comboType = context.analytics.comboCorrelationToType[respCorrelationId];
  const isComboResponse = !!comboType;

  switch (resp.code) {
    case "bet-accepted":
    case "bet-partial-stake-accepted": {
      triggerHaptic.success();
      const result = buildAcceptedResult(resp);
      if (isComboResponse) {
        store.setCombinationResult(comboType, {
          betId: resp.data.betId,
          entryId: resp.data.entryId,
        });
      } else if (isMultiResponse) {
        store.setMultiResult({
          ...result,
          oddsByLeg: toOddsByLeg(
            resp.data.selections,
            context.correlationToOutcome,
          ),
        });
        // The token was consumed by this bet — clear it so Reuse Bets
        // can't attempt the same claim twice.
        store.setMultiAllocation(null);
      } else {
        const key = resolveSelectionKey(context, outcomeIds, respCorrelationId);
        if (key) {
          store.setSelectionResult(key, result);
          store.setSingleAllocation(key, null);
        }
      }
      // Partial-stake accepts don't emit their own telemetry event.
      if (resp.code === "bet-accepted") {
        fireBetSubmittedTelemetry(resp, "Accepted", context);
      }
      break;
    }

    case "bet-rejected": {
      triggerHaptic.error();
      const rejection = buildRejection(resp);
      const result: BetResult = { status: "rejected", rejection };

      // Capture the betId so the next submit can pass it back as the
      // alternativeStakeReference.
      if (resp.data.alternativeStake !== undefined && resp.data.betId) {
        if (isMultiResponse) {
          store.setMultiAlternativeStakeReference(resp.data.betId);
        } else if (!isComboResponse) {
          const key = resolveSelectionKey(
            context,
            outcomeIds,
            respCorrelationId,
          );
          if (key) store.setAlternativeStakeReference(key, resp.data.betId);
        }
      }

      if (isComboResponse) {
        store.setCombinationResult(comboType, { rejected: rejection });
      } else if (isMultiResponse) {
        applyMultiRejection(resp, result, context);
      } else {
        const key = resolveSelectionKey(context, outcomeIds, respCorrelationId);
        if (key) store.setSelectionResult(key, result);
      }
      fireBetSubmittedTelemetry(resp, "Rejected", context);
      break;
    }

    // bet-intercepted is handled at the orchestration level, not per-response.
    default:
      break;
  }
};

const applyMultiRejection = (
  resp: RejectedBetResponse,
  result: BetResult,
  context: ProcessContext,
): void => {
  const store = context.storeHandle.getState();
  let perLegCount = 0;
  let orphanCount = 0;

  for (const err of resp.errors ?? []) {
    const corr = err.propertyPath?.match(/^selections\.([^.]+)/)?.[1];
    const outcomeId = corr ? context.correlationToOutcome[corr] : undefined;
    if (outcomeId) {
      perLegCount++;
      store.setMultiLegRejection(outcomeId, {
        reason: parseRejectionReason(err.messageTemplate),
        message: err.message,
      });
    } else {
      orphanCount++;
    }
  }

  if (orphanCount > 0 || perLegCount === 0) {
    store.setMultiResult(
      (resp.errors?.length ?? 0) === 0
        ? {
            status: "rejected",
            rejection: {
              reason: "multi rejected without error data",
              message: "Something went wrong",
            },
          }
        : result,
    );
  }
};
